import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Form,
} from "react-bootstrap";
import { authAPI } from "../services/api";
import { useLanguage } from "../i18n/LanguageContext";
import PasswordInput, { isPasswordValid } from "./PasswordInput";

const EyeOpen = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeClosed = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

interface ForceChangePasswordProps {
  /** The temporary password the user just logged in with */
  currentPassword: string;
  onSuccess: () => void;
}

const ForceChangePassword: React.FC<ForceChangePasswordProps> = ({
  currentPassword,
  onSuccess,
}) => {
  const { t, dir } = useLanguage();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const passwordValid = isPasswordValid(newPassword);
  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit =
    passwordValid && passwordsMatch && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      onSuccess();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          "Failed to change password. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      fluid
      className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: "#f8f9fa" }}
      dir={dir}
    >
      <Row className="w-100 justify-content-center px-3">
        <Col xs={12} sm={10} md={6} lg={4} xl={3}>
          <Card className="shadow border-0" style={{ borderRadius: "16px" }}>
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <div style={{ fontSize: 48 }}>🔑</div>
                <h4 className="mb-1" style={{ fontWeight: 600 }}>
                  {t("changePassword")}
                </h4>
                <p className="text-muted small mb-0">
                  {t("mustChangePasswordDesc")}
                </p>
              </div>

              {error && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setError("")}
                  className="py-2 small"
                >
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <PasswordInput
                  value={newPassword}
                  onChange={setNewPassword}
                  label={t("newPassword")}
                  placeholder={t("newPassword")}
                  showStrength
                  controlId="newPassword"
                  required
                  disabled={loading}
                />

                {/* Confirm password */}
                <Form.Group className="mb-3" controlId="confirmNewPassword">
                  <Form.Label className="small fw-semibold">
                    {t("confirmPassword")}
                  </Form.Label>
                  <div style={{ position: "relative" }}>
                    <Form.Control
                      type={showConfirm ? "text" : "password"}
                      placeholder={t("confirmPassword")}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      isInvalid={confirmPassword.length > 0 && !passwordsMatch}
                      isValid={confirmPassword.length > 0 && passwordsMatch}
                      style={{
                        padding: "12px 44px 12px 12px",
                        borderRadius: "8px",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      disabled={loading}
                      tabIndex={-1}
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 42,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#6c757d",
                        borderRadius: "0 8px 8px 0",
                        padding: 0,
                      }}
                    >
                      {showConfirm ? <EyeClosed /> : <EyeOpen />}
                    </button>
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <div
                        className="invalid-feedback"
                        style={{ display: "block" }}
                      >
                        {t("passwordsDoNotMatch")}
                      </div>
                    )}
                  </div>
                </Form.Group>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={!canSubmit || loading}
                    style={{
                      padding: "12px",
                      borderRadius: "8px",
                      fontWeight: 600,
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        {t("saving")}
                      </>
                    ) : (
                      t("changePassword")
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ForceChangePassword;
